import { useEffect, useState } from "react";
import { fetchDropAnalytics } from "../../services/dropAnalyticsService";
import "./DropAnalyticsModal.css";

function formatDuration(ms) {
  if (ms == null || ms < 0) return "—";
  const totalMin = Math.round(ms / 60000);
  if (totalMin < 1) return "< 1 min";
  if (totalMin < 60) return `${totalMin} min`;
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  if (h < 48) return m > 0 ? `${h}h ${m}m` : `${h}h`;
  const d = Math.floor(h / 24);
  const rh = h % 24;
  return rh > 0 ? `${d}d ${rh}h` : `${d}d`;
}

function formatMoneyCents(cents) {
  const n = (Number(cents) || 0) / 100;
  return `$${n.toFixed(2)}`;
}

function formatLocalDateTime(ms) {
  if (!ms) return "—";
  return new Date(ms).toLocaleString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function formatLocalTime(ms) {
  if (!ms) return "—";
  return new Date(ms).toLocaleTimeString(undefined, {
    hour: "numeric",
    minute: "2-digit",
  });
}

function contentLabel(type) {
  const map = { video: "Video", photo: "Photo", audio: "Audio", article: "Article" };
  return map[type] || "Drop";
}

function isPaidArchive(afterClose) {
  return afterClose === "archive" || afterClose === "paid_archive";
}

function scenarioKey(data) {
  if (!data) return "live";
  if (data.status !== "closed") return "live";
  if (isPaidArchive(data.afterClose)) return "closed-archive";
  return "closed-forever";
}

function PaceChart({ buckets }) {
  if (!buckets?.length) {
    return <div className="ap-bars-empty">No view pace data yet — check back as fans unlock.</div>;
  }

  const max = Math.max(...buckets.map((b) => b.count), 1);
  // Cap labels to avoid clutter on long fills
  const step = buckets.length > 12 ? Math.ceil(buckets.length / 8) : 1;

  return (
    <div className="ap-bars" aria-hidden="true">
      {buckets.map((b, i) => {
        const h = Math.max(2, Math.round((b.count / max) * 88));
        const isPeak = b.count === max && b.count > 0;
        const showLabel = i === 0 || i === buckets.length - 1 || i % step === 0;
        return (
          <div key={`${b.offsetMin}-${i}`} className="ap-bar-wrap" title={`${b.count} views · +${b.offsetMin}m`}>
            <div
              className={`ap-bar${isPeak ? " ap-bar--peak" : ""}`}
              style={{ height: `${h}px` }}
            />
            {showLabel && <span className="ap-bar-label">+{b.offsetMin}m</span>}
          </div>
        );
      })}
    </div>
  );
}

export default function DropAnalyticsModal({ open, onClose, drop }) {
  const dropId = drop?.id || drop?.momentId;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open || !dropId) return undefined;
    let cancelled = false;
    setLoading(true);
    setError("");
    setData(null);

    fetchDropAnalytics(dropId)
      .then((payload) => {
        if (!cancelled) setData(payload);
      })
      .catch((err) => {
        if (!cancelled) setError(err.message || "Could not load analytics.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, dropId]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose]);

  if (!open) return null;

  const scenario = scenarioKey(data);
  const typeLabel = contentLabel(data?.contentType || drop?.contentType);
  const feePct = Math.round((Number(data?.feeRate) || 0.15) * 100);
  const feeCents = Math.round(
    ((Number(data?.first100GrossCents) || 0) + (Number(data?.archiveGrossCents) || 0))
      * (Number(data?.feeRate) || 0.15)
  );

  let heroValue = "—";
  let heroNote = "";
  let heroHand = "";
  if (data) {
    if (scenario === "live") {
      heroValue = "Still filling";
      if (data.estimatedFillMs != null) {
        heroNote = `At the current pace, the first ${data.maxViews} could fill in about ${formatDuration(data.estimatedFillMs)}.`;
        heroHand = "— estimate updates as views come in";
      } else if ((data.viewCount || 0) === 0) {
        heroNote = "No confirmed views yet. Share the drop to start the clock.";
        heroHand = "— fill speed starts with view #1";
      } else {
        heroNote = `${data.viewCount} of ${data.maxViews} spots claimed. Pace is still settling.`;
      }
    } else {
      heroValue = formatDuration(data.fillDurationMs);
      heroNote =
        scenario === "closed-archive"
          ? `First ${data.maxViews} filled — drop stays available in archive.`
          : `First ${data.maxViews} filled — locked forever after that.`;
      heroHand = "— sold-out clock from post to last first-100 view";
    }
  }

  const statusClass =
    scenario === "live"
      ? "ap-status--live"
      : scenario === "closed-archive"
        ? "ap-status--archive"
        : "ap-status--closed";
  const statusText =
    scenario === "live"
      ? "Live · filling"
      : scenario === "closed-archive"
        ? "Closed · archive open"
        : "Closed · gone forever";

  return (
    <div className="ap-backdrop" onClick={onClose} role="presentation">
      <div
        className="ap-panel"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="ap-title"
      >
        <header className="ap-header">
          <div>
            <span className="ap-eyebrow">{typeLabel} analytics</span>
            <h2 id="ap-title" className="ap-title">
              Fill <em>speed</em>
            </h2>
            <p className="ap-sub">{data?.title || drop?.title || "Untitled drop"}</p>
          </div>
          <button type="button" className="ap-close" onClick={onClose} aria-label="Close">
            ×
          </button>
        </header>

        <div className="ap-body">
          {loading && <div className="ap-loading">Loading analytics…</div>}

          {!loading && error && (
            <div className="ap-error">
              {error}
              <div>
                <button
                  type="button"
                  className="ap-retry"
                  onClick={() => {
                    setError("");
                    setLoading(true);
                    fetchDropAnalytics(dropId)
                      .then(setData)
                      .catch((err) => setError(err.message || "Could not load analytics."))
                      .finally(() => setLoading(false));
                  }}
                >
                  Try again
                </button>
              </div>
            </div>
          )}

          {!loading && !error && data && (
            <>
              <span className={`ap-status ${statusClass}`}>
                <span className="ap-status-dot" />
                {statusText}
              </span>

              <section className="ap-hero">
                <div className="ap-hero-label">Fill speed</div>
                <div className="ap-hero-value">
                  {scenario === "live" ? (
                    <>Still <em>filling</em></>
                  ) : (
                    heroValue
                  )}
                </div>
                <p className="ap-hero-note">{heroNote}</p>
                {heroHand ? <span className="ap-hero-hand">{heroHand}</span> : null}
              </section>

              <div className="ap-stat-grid">
                <div className="ap-stat">
                  <div className="ap-stat-label">Posted</div>
                  <div className="ap-stat-value ap-stat-value--sm">
                    {formatLocalTime(data.createdAtMs)}
                  </div>
                  <div className="ap-stat-hint">{formatLocalDateTime(data.createdAtMs)}</div>
                </div>

                {scenario === "live" ? (
                  <div className="ap-stat">
                    <div className="ap-stat-label">Current pace</div>
                    <div className="ap-stat-value">
                      {data.livePacePerHour != null ? data.livePacePerHour : data.avgPacePerHour}
                    </div>
                    <div className="ap-stat-hint">views / hour</div>
                  </div>
                ) : (
                  <div className="ap-stat">
                    <div className="ap-stat-label">Sold out</div>
                    <div className="ap-stat-value ap-stat-value--sm">
                      {formatLocalTime(data.soldOutAtMs)}
                    </div>
                    <div className="ap-stat-hint">{formatLocalDateTime(data.soldOutAtMs)}</div>
                  </div>
                )}

                <div className="ap-stat">
                  <div className="ap-stat-label">Peak pace</div>
                  <div className="ap-stat-value">{data.peakPacePerHour || 0}</div>
                  <div className="ap-stat-hint">views / hour (best 10 min)</div>
                </div>
              </div>

              <section className="ap-section">
                <h3 className="ap-section-title">Earnings</h3>
                <p className="ap-section-sub">
                  First-{data.maxViews} window
                  {scenario === "closed-archive" ? " + archive sales" : ""}
                  , after your {feePct}% platform fee.
                </p>
                <div className="ap-earn-row">
                  <span className="ap-earn-label">
                    First {data.maxViews} · {data.viewCount}/{data.maxViews} @ ${Number(data.priceCad || 0).toFixed(2)}
                  </span>
                  <span className="ap-earn-value">{formatMoneyCents(data.first100GrossCents)}</span>
                </div>
                {scenario === "closed-archive" && (
                  <div className="ap-earn-row">
                    <span className="ap-earn-label">
                      Archive · {data.archiveSalesCount || 0} sale{(data.archiveSalesCount || 0) === 1 ? "" : "s"} @ $
                      {Number(data.archivePriceCad || 0).toFixed(2)}
                    </span>
                    <span className="ap-earn-value">{formatMoneyCents(data.archiveGrossCents)}</span>
                  </div>
                )}
                {scenario === "closed-forever" && (
                  <div className="ap-earn-row">
                    <span className="ap-earn-label">After close</span>
                    <span className="ap-earn-value">Locked forever · $0</span>
                  </div>
                )}
                {scenario === "live" && (
                  <div className="ap-earn-row">
                    <span className="ap-earn-label">Still open</span>
                    <span className="ap-earn-value">Gross so far</span>
                  </div>
                )}
                <div className="ap-earn-row ap-earn-row--fee">
                  <span className="ap-earn-label">Platform fee ({feePct}%)</span>
                  <span className="ap-earn-value">−{formatMoneyCents(feeCents)}</span>
                </div>
                <div className="ap-earn-row ap-earn-row--net">
                  <span className="ap-earn-label">Your net</span>
                  <span className="ap-earn-value">{formatMoneyCents(data.netEarningsCents)}</span>
                </div>
              </section>

              <section className="ap-section">
                <h3 className="ap-section-title">View pace</h3>
                <p className="ap-section-sub">Confirmed first views in 10-minute buckets from post time.</p>
                <PaceChart buckets={data.viewPaceBuckets} />
              </section>

              <section className="ap-section">
                <h3 className="ap-section-title">Time of day</h3>
                <p className="ap-section-sub">Shown in your local timezone.</p>
                <div className="ap-tod-row">
                  <div className={`ap-tod-card${scenario === "live" ? " ap-tod-card--full" : ""}`}>
                    <div className="ap-tod-label">Posted</div>
                    <div className="ap-tod-value">{formatLocalDateTime(data.createdAtMs)}</div>
                    <div className="ap-tod-hint">{formatLocalTime(data.createdAtMs)}</div>
                  </div>
                  {scenario !== "live" && (
                    <div className="ap-tod-card">
                      <div className="ap-tod-label">
                        {scenario === "closed-archive" ? "First 100 filled" : "Sold out"}
                      </div>
                      <div className="ap-tod-value">{formatLocalDateTime(data.soldOutAtMs)}</div>
                      <div className="ap-tod-hint">{formatLocalTime(data.soldOutAtMs)}</div>
                    </div>
                  )}
                </div>
              </section>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
